import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export default function Settings() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "general");
  const queryClient = useQueryClient();
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    preference: true,
  });

  const [hideAvatarFromDirectory, setHideAvatarFromDirectory] = useState(false);
  const [hideAvatarFromFundraising, setHideAvatarFromFundraising] = useState(true);

  // Fetch user roles
  const { data: userRoles } = useQuery({
    queryKey: ["userRoles", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Fetch member data including PTA admin status
  const { data: memberData } = useQuery({
    queryKey: ["memberData", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("members")
        .select("id, is_pta_admin")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch member additional access
  const { data: memberAccess } = useQuery({
    queryKey: ["memberAccess", user?.id, memberData?.id],
    queryFn: async () => {
      if (!memberData?.id) return null;

      const { data, error } = await supabase
        .from("member_additional_access")
        .select("*")
        .eq("member_id", memberData.id);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!memberData?.id,
  });

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
  
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (profile) {
      setHideAvatarFromDirectory(profile.hide_avatar_from_directory || false);
      setHideAvatarFromFundraising(profile.hide_avatar_from_fundraising ?? true);
    }
  }, [profile]);

  
  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    const formData = new FormData(e.target as HTMLFormElement);
    const fullName = formData.get('full_name') as string;

    const { error } = await supabase
      .from('profiles')
      .update({ 
        full_name: fullName
      })
      .eq('id', user.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
      
      toast({
        title: "Profile updated",
        description: "Your profile information has been saved.",
      });
    }
  };
  
  const handleSavePrivacy = async () => {
    if (!user?.id) return;

    const { error } = await supabase
      .from('profiles')
      .update({ 
        hide_avatar_from_directory: hideAvatarFromDirectory,
        hide_avatar_from_fundraising: hideAvatarFromFundraising
      })
      .eq('id', user.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Privacy settings saved",
        description: "Your privacy preferences have been updated.",
      });
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size must be less than 2MB",
        variant: "destructive",
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please upload an image file (JPG, PNG, or GIF)",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingAvatar(true);

    try {
      // Delete old avatar if exists
      if (profile?.avatar_url) {
        const oldPath = profile.avatar_url.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from('avatars')
            .remove([`${user.id}/${oldPath}`]);
        }
      }

      // Upload new avatar
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["profile", user.id] });

      toast({
        title: "Success",
        description: "Avatar updated successfully",
      });
    } catch (error: any) {
      console.error('Avatar upload error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload avatar",
        variant: "destructive",
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-2">
          Manage your PTA configuration and preferences
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="roles">My Roles</TabsTrigger>
          <TabsTrigger value="privacy">Privacy</TabsTrigger>
        </TabsList>


        <TabsContent value="profile" className="space-y-4">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Profile Settings</CardTitle>
              <CardDescription>
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div className="flex items-center gap-4 mb-6">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={profile?.avatar_url} />
                    <AvatarFallback className="text-lg">
                      {user?.email?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <input
                      type="file"
                      id="avatar-upload"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarUpload}
                      disabled={isUploadingAvatar}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('avatar-upload')?.click()}
                      disabled={isUploadingAvatar}
                    >
                      {isUploadingAvatar ? "Uploading..." : "Change Avatar"}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPG, PNG or GIF. Max size 2MB
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Full Name</Label>
                  <Input 
                    id="profile-name" 
                    name="full_name"
                    defaultValue={profile?.full_name || ""} 
                    placeholder="Your full name" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-email">Email</Label>
                  <Input 
                    id="profile-email" 
                    type="email" 
                    defaultValue={user?.email || ""} 
                    disabled 
                  />
                  <p className="text-xs text-muted-foreground">
                    Contact support to change your email address
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-phone">Phone Number</Label>
                  <Input id="profile-phone" placeholder="+44 123 456 7890" />
                </div>
                <Button type="submit">Save Changes</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="space-y-4">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>My Roles & Permissions</CardTitle>
              <CardDescription>
                View your assigned roles and permissions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* PTA Admin role - displayed prominently if user has it */}
              {memberData?.is_pta_admin && (
                <div className="space-y-3">
                  <h3 className="font-medium text-sm">Administrative Role</h3>
                  <div className="p-4 border-2 border-primary/50 bg-primary/5 rounded-lg">
                    <h4 className="font-semibold text-primary">PTA Admin</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Full administrative access to manage PTA settings, members, and subscriptions
                    </p>
                  </div>
                </div>
              )}

              {userRoles && userRoles.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="font-medium text-sm">Assigned Roles</h3>
                  {userRoles.map((roleData: any) => (
                    <div key={roleData.role} className="p-4 border border-border rounded-lg">
                      <h4 className="font-semibold capitalize">{roleData.role.replace('_', ' ')}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {roleData.role === 'chair' && 'Lead the PTA, manage meetings, oversee all operations'}
                        {roleData.role === 'treasurer' && 'Manage finances, expenses, and cash floats'}
                        {roleData.role === 'secretary' && 'Handle communications, meetings, and documentation'}
                        {roleData.role === 'trustee' && 'Provide governance, strategic oversight, and ensure charitable compliance'}
                        {roleData.role === 'admin' && 'Full administrative access to PTA management'}
                        {roleData.role === 'site_admin' && 'Manage site-wide settings and configurations'}
                        {roleData.role === 'catering' && 'Manage catering stock, menu items, and event food coordination'}
                        {roleData.role === 'communications' && 'Handle newsletters, announcements, and PTA communications'}
                        {roleData.role === 'community' && 'Manage community engagement, donors, and sponsorships'}
                        {roleData.role === 'events' && 'Plan and coordinate PTA events and activities'}
                        {roleData.role === 'school_liaison' && 'Coordinate between the PTA and school administration'}
                        {roleData.role === 'shop' && 'Manage the PTA shop, inventory, and sales'}
                        {roleData.role === 'assets' && 'Track and manage PTA equipment and assets'}
                        {roleData.role === 'expenses' && 'Submit and track expense claims'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : !memberData?.is_pta_admin ? (
                <p className="text-sm text-muted-foreground">No roles assigned yet</p>
              ) : null}

              {memberAccess && memberAccess.length > 0 && (
                <div className="space-y-3 mt-6">
                  <h3 className="font-medium text-sm">Additional Permissions</h3>
                  {memberAccess.map((access: any) => (
                    <div key={access.id} className="p-4 border border-border rounded-lg">
                      <h4 className="font-semibold capitalize">{access.access_type.replace('_', ' ')}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {access.access_type === 'documents' && 'Access and manage PTA documents'}
                        {access.access_type === 'expenses' && 'Submit and manage expense claims'}
                      </p>
                      {access.permission_level && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Permission Level: <span className="font-medium">{access.permission_level}</span>
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="privacy" className="space-y-4">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Privacy Settings</CardTitle>
              <CardDescription>
                Manage your privacy and cookie preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium text-sm">Cookie Preferences</h3>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="essential-cookies">Essential Cookies</Label>
                    <p className="text-sm text-muted-foreground">
                      Required for the website to function properly
                    </p>
                  </div>
                  <Switch 
                    id="essential-cookies" 
                    checked={cookiePreferences.essential}
                    disabled
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="analytics-cookies">Analytics Cookies</Label>
                    <p className="text-sm text-muted-foreground">
                      Help us understand how you use our platform
                    </p>
                  </div>
                  <Switch 
                    id="analytics-cookies" 
                    checked={cookiePreferences.analytics}
                    onCheckedChange={(checked) => 
                      setCookiePreferences(prev => ({ ...prev, analytics: checked }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="marketing-cookies">Marketing Cookies</Label>
                    <p className="text-sm text-muted-foreground">
                      Used to deliver personalized advertisements
                    </p>
                  </div>
                  <Switch 
                    id="marketing-cookies" 
                    checked={cookiePreferences.marketing}
                    onCheckedChange={(checked) => 
                      setCookiePreferences(prev => ({ ...prev, marketing: checked }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="preference-cookies">Preference Cookies</Label>
                    <p className="text-sm text-muted-foreground">
                      Remember your preferences and settings
                    </p>
                  </div>
                  <Switch 
                    id="preference-cookies" 
                    checked={cookiePreferences.preference}
                    onCheckedChange={(checked) => 
                      setCookiePreferences(prev => ({ ...prev, preference: checked }))
                    }
                  />
                </div>
              </div>

              <Separator className="my-4" />

              <div className="space-y-4">
                <h3 className="font-medium text-sm">Other Preferences</h3>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="hide-avatar">Hide Avatar from Directory</Label>
                    <p className="text-sm text-muted-foreground">
                      Your avatar will not be visible in the member directory
                    </p>
                  </div>
                  <Switch 
                    id="hide-avatar" 
                    checked={hideAvatarFromDirectory}
                    onCheckedChange={setHideAvatarFromDirectory}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="hide-avatar-fundraising">Hide Avatar from Fundraising Page</Label>
                    <p className="text-sm text-muted-foreground">
                      Your avatar will not be visible on the public fundraising page
                    </p>
                  </div>
                  <Switch 
                    id="hide-avatar-fundraising" 
                    checked={hideAvatarFromFundraising}
                    onCheckedChange={setHideAvatarFromFundraising}
                  />
                </div>
              </div>

              <Button onClick={handleSavePrivacy}>Save Privacy Settings</Button>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
